import React, { useEffect, useRef, useState } from "react";
import { Download, Check, Info, Pencil, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { fmtGbp } from "@/lib/constants";
import { toast } from "sonner";

export function CarriedForwardPanel({ step, engagementId, locked, onValues }) {
    const rows = (step.inputs || []).filter((f) => (f.key || "").startsWith("bf_"));
    const [vals, setVals] = useState(step.values || {});
    const [auto, setAuto] = useState(null);
    const [pulling, setPulling] = useState(false);
    const prefilled = useRef(false);

    useEffect(() => { setVals(step.values || {}); }, [step.values]);

    const save = (next) => { setVals(next); onValues(step, next); };
    const setField = (k, v) => save({ ...vals, [k]: v });
    const setAmount = (k, v) => setVals((p) => ({ ...p, [k]: v, [`${k}__manual`]: true }));  // local only; persisted on blur
    const persist = () => onValues(step, vals);
    const na = !!vals.bf_na;

    // Load prior-year TB figures; pre-fill any blank amount from the imported PY TB once.
    useEffect(() => {
        api.get(`/engagements/${engagementId}/bf-balances`).then((r) => {
            setAuto(r.data);
            if (prefilled.current || locked || na || !r.data?._available) return;
            prefilled.current = true;
            const cur = step.values || {};
            const next = { ...cur };
            let changed = false;
            rows.forEach((f) => {
                const a = r.data[f.key];
                const empty = cur[f.key] === undefined || cur[f.key] === null || String(cur[f.key]).trim() === "";
                if (empty && a !== undefined && a !== null) { next[f.key] = String(a); changed = true; }
            });
            if (changed) save(next);
        }).catch(() => setAuto({ _available: false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engagementId]);

    const available = auto?._available;
    const pull = async () => {
        setPulling(true);
        try {
            const r = await api.get(`/engagements/${engagementId}/bf-balances`);
            setAuto(r.data);
            const next = { ...vals };
            rows.forEach((f) => {
                const a = r.data[f.key];
                if (a !== undefined && a !== null) { next[f.key] = String(a); next[`${f.key}__manual`] = false; }
            });
            save(next);
            toast.success("Pulled from prior-year trial balance");
        } catch { toast.error("Could not read the prior-year trial balance"); }
        finally { setPulling(false); }
    };

    const allAgreed = !na && rows.length > 0 && rows.every((f) => vals[`${f.key}__agreed`]);

    return (
        <div className="ml-5 mt-2 border border-border rounded-sm overflow-hidden" data-testid="bf-panel">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-border flex-wrap">
                <span className="text-[11px] uppercase tracking-widest font-display font-semibold text-muted-foreground">Key carried-forward balances</span>
                {allAgreed && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-sm" data-testid="bf-all-agreed">All agreed</span>}
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none" data-testid="bf-na-label">
                    <input type="checkbox" data-testid="bf-na-toggle" disabled={locked} checked={na}
                        onChange={(e) => setField("bf_na", e.target.checked)} className="accent-slate-600 w-3.5 h-3.5" />
                    Not applicable — first-year / no prior-year accounts prepared
                </label>
                {!na && (
                    <button data-testid="bf-pull" onClick={pull} disabled={locked || pulling || !available}
                        className="ml-auto flex items-center gap-1.5 text-[11px] font-medium border border-brand/30 text-brand hover:bg-brand/5 px-2.5 py-1 rounded-sm disabled:opacity-40">
                        <Download className="w-3.5 h-3.5" /> {pulling ? "Pulling…" : "Pull from prior-year TB"}
                    </button>
                )}
            </div>

            {na ? (
                <div className="flex items-start gap-2 px-3 py-3 bg-amber-50 text-amber-900 text-[11px]" data-testid="bf-na-note">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Marked <strong>not applicable</strong> — there are no prior-year accounts to agree to (first-year engagement or no prior-year accounts prepared). <strong>Consider whether to disclaim on opening balances</strong> (see B01 / ISA UK 510) and document the reasoning in the response box below.</span>
                </div>
            ) : (
                <>
                    {auto && !available && (
                        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 text-amber-900 text-[11px] border-b border-amber-200" data-testid="bf-import-prompt">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span>No prior-year trial balance imported — enter each balance manually below (agree to the prior-year signed accounts attached earlier in B). Importing the <strong>prior-year trial balance</strong> (Trial Balance → import with comparatives) will auto-populate these figures.</span>
                        </div>
                    )}
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-white">
                                <th className="px-3 py-1.5">Balance</th>
                                <th className="num text-right">Amount</th>
                                <th className="text-center w-48">Agrees to PY signed accounts</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((f) => {
                                const amount = vals[f.key];
                                const agreed = !!vals[`${f.key}__agreed`];
                                const manual = !!vals[`${f.key}__manual`];
                                return (
                                    <tr key={f.key} className={`border-b border-border/50 ${agreed ? "bg-emerald-50/60" : ""}`} data-testid={`bf-row-${f.key}`}>
                                        <td className="px-3 py-1.5">
                                            <div>{f.label}</div>
                                            {manual && <span className="inline-flex items-center gap-1 text-[9px] text-amber-700 mt-0.5" data-testid={`bf-manual-${f.key}`}><Pencil className="w-2.5 h-2.5" /> Manually entered</span>}
                                        </td>
                                        <td className="num text-right pr-2">
                                            <div className="inline-flex items-center border border-input rounded-sm bg-white focus-within:border-brand">
                                                <span className="pl-1.5 text-muted-foreground">£</span>
                                                <input data-testid={`bf-amount-${f.key}`} disabled={locked} value={amount ?? ""}
                                                    onChange={(e) => setAmount(f.key, e.target.value)} onBlur={persist}
                                                    className="w-28 px-1.5 py-1 text-right font-num bg-transparent outline-none disabled:bg-slate-50" />
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <button data-testid={`bf-agreed-${f.key}`} disabled={locked} onClick={() => setField(`${f.key}__agreed`, !agreed)}
                                                className={`w-5 h-5 rounded-sm border inline-flex items-center justify-center ${agreed ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 bg-white hover:border-emerald-500"} disabled:opacity-50`}>
                                                {agreed && <Check className="w-3.5 h-3.5" />}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p className="text-[10px] text-muted-foreground px-3 py-1.5">Amounts are pre-filled from the imported prior-year trial balance (or entered manually). Tick each balance once agreed to the prior-year signed accounts. Note any balance not agreed in the response box below.</p>
                </>
            )}
        </div>
    );
}
